import * as Config from '../data/Config';
import { s3, lambda } from './AWSConfig';
import RecordType from '../data/RecordType';
import { getFileTypeFromExtension } from '../data/FileType';
import { ManagedUpload } from 'aws-sdk/clients/s3';
import { reportBFError } from './Tracker';
import { AWSError } from 'aws-sdk';
import { newUUID } from 'utils';

export const getBaseURL = () => {
  return (
    'https://s3-' +
    Config.S3_BUCKET_REGION +
    '.amazonaws.com/' +
    Config.S3_BUCKET_NAME +
    '/'
  );
};

export const isUrlOnAWS = (url: string): boolean => {
  return url.includes('.amazonaws.com');
};

export const isTemplateAsset = (url: string): boolean => {
  return (
    url.includes('/loops/') ||
    url.includes('/examples/') ||
    url.includes('/templates/')
  );
};

const hasFile = async (filename: string): Promise<boolean> => {
  try {
    await s3
      .headObject({ Bucket: Config.S3_BUCKET_NAME, Key: filename })
      .promise();
    return true;
  } catch (e) {
    return false;
  }
};

export const getAvailableFilenameWithExtension = async (
  ext: string
): Promise<string> => {
  let filename = newUUID();
  while (await hasFile(`${filename}.${ext}`)) {
    console.log(`already has file "${filename}". Will generate a new one.`);
    filename = newUUID();
  }
  return filename;
};

export const getAvailableFilename = async (
  type: RecordType
): Promise<string> => {
  const ext = type.startsWith('video') ? 'mp4' : 'gif';
  return await getAvailableFilenameWithExtension(ext);
};

export const uploadFile = async (
  Key: string,
  Body: File,
  ContentType: string,
  progressCallback: ((progress: number) => void) | null = null
): Promise<void> => {
  return new Promise((resolve, reject) => {
    const request = s3.upload(
      {
        Key,
        Body,
        ACL: 'public-read',
        ContentType,
        Bucket: Config.S3_BUCKET_NAME
      },
      (err: any) => {
        if (err) {
          reportBFError(err);
          reject();
        } else {
          resolve();
        }
      }
    );
    if (progressCallback) {
      request.on('httpUploadProgress', (progress: any) => {
        const perc = progress.loaded / progress.total;
        progressCallback(perc);
      });
    }
  });
};

export const uploadFileCancellable = (
  Key: string,
  Body: File,
  ContentType: string,
  successCallback: () => void,
  failedCallback: (error: string) => void,
  progressCallback: ((progress: number) => void) | null = null
): ManagedUpload => {
  const request = s3.upload(
    {
      Key,
      Body,
      ACL: 'public-read',
      ContentType,
      Bucket: Config.S3_BUCKET_NAME
    },
    (err: any) => {
      if (err) {
        reportBFError(err);
        failedCallback(err.toString());
      } else {
        successCallback();
      }
    }
  );
  if (progressCallback) {
    request.on('httpUploadProgress', (progress: any) => {
      const perc = progress.loaded / progress.total;
      progressCallback(perc);
    });
  }
  return request;
};

export const uploadAsset = async (file: File): Promise<string> => {
  console.log('upload asset: ' + file.name);
  let extension = (file.name.split('.').pop() as string).toLowerCase();
  if (extension === 'm4a' || extension === 'aac') {
    extension = 'mp4';
  }
  let filename = await getAvailableFilenameWithExtension(extension);
  let fullFilename = `${filename}.${extension}`;
  let contentType = getFileTypeFromExtension(extension);
  await uploadFile(fullFilename, file, contentType);
  return getBaseURL() + fullFilename;
};

export const deleteFile = async (Key: string): Promise<void> => {
  console.log(`delete file: ${Key}`);
  await s3
    .deleteObject({
      Bucket: Config.S3_BUCKET_NAME,
      Key
    })
    .promise();
};

export const deleteFileWithUrl = async (url: string) => {
  const key = url.split('/').pop();
  await deleteFile(key!);
};

export const duplicateFile = async (
  name: string,
  newName: string
): Promise<string> => {
  await s3
    .copyObject({
      CopySource: `${Config.S3_BUCKET_NAME}/${name}`,
      Bucket: Config.S3_BUCKET_NAME,
      Key: newName
    })
    .promise();
  return getBaseURL() + newName;
};

export const renameFile = async (oldName: string, newName: string) => {
  await duplicateFile(oldName, newName);
  await deleteFile(oldName);
};

export const convertVideo = (
  videoUrl: string,
  audioUrl?: string,
  frameRate?: number
): Promise<string> => {
  return new Promise((resolve, reject) => {
    const Payload = JSON.stringify({
      videoUrl,
      audioUrl,
      frameRate
    });
    lambda.invoke(
      {
        FunctionName: 'VideoConverter',
        InvocationType: 'RequestResponse',
        Payload
      },
      (err: AWSError, data: any) => {
        // console.log(data);
        if (err) {
          reportBFError(err);
          reject(err.message);
        } else if (data.StatusCode !== 200) {
          reportBFError(new Error(data.Payload));
          reject(`Returned wrong status code ${data.StatusCode}`);
        } else {
          const payload = JSON.parse(data.Payload);
          if (payload.body) {
            resolve(payload.body);
          } else {
            reportBFError(new Error(data.Payload));
            reject(`Could not convert video with error ${data.Payload}`);
          }
        }
      }
    );
  });
};

export const renderVideo = (flyerId: string): Promise<any> => {
  return new Promise((resolve, reject) => {
    lambda.invoke(
      {
        FunctionName: 'invokationTest',
        InvocationType: 'Event',
        Payload: JSON.stringify({
          flyerId
        })
      },
      (err: AWSError, data: any) => {
        // console.log(data);
        if (err) {
          reject(err.message);
        } else if (data.StatusCode !== 202) {
          reject(`Returned wrong status code ${data.StatusCode}`);
        } else {
          resolve(data);
        }
      }
    );
  });
};

export const getFileNameFromUrl = (url: string): string => {
  if (!isUrlOnAWS) {
    throw new Error('url is not on AWS');
  }
  if (url.indexOf(Config.S3_BUCKET_NAME) === -1) {
    throw new Error("url doesn't contain bucket name");
  }
  return url.split(`${Config.S3_BUCKET_NAME}/`)[1];
};



// WEBPACK FOOTER //
// ./src/utils/S3Utils.ts